import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Plus, TrendingUp, Users, DollarSign, Target, Filter, Search, Calendar, Eye, Edit, Trash2 } from 'lucide-react';
import { mountComponent } from './Application';


document.addEventListener('DOMContentLoaded', () => {
    mountComponent('panel', Panel);
});

const Panel = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [selectedBusiness, setSelectedBusiness] = useState('business-1');
    const [dateRange, setDateRange] = useState({ from: '2024-01-01', to: '2024-12-31' });

    // Mock data - en tu aplicación real vendría de tu API
    const [metrics, setMetrics] = useState({
        revenue: 125000,
        spent: 45000,
        leads: 850,
        clients: 187,
        roas: 2.78,
        cac: 240.64,
        conversionRate: 22.0,
        mrr: 15600
    });

    const [leads, setLeads] = useState([
        { id: '1', name: 'María González', phone: '+593987654321', email: 'maria@email.com', source: 'Facebook', status: 'New', createdAt: '2024-07-20', estimatedValue: 500 },
        { id: '2', name: 'Carlos Ruiz', phone: '+593987654322', email: 'carlos@email.com', source: 'Instagram', status: 'Converted', createdAt: '2024-07-18', estimatedValue: 800, convertedAt: '2024-07-25' },
        { id: '3', name: 'Ana López', phone: '+593987654323', email: 'ana@email.com', source: 'WhatsApp', status: 'Qualified', createdAt: '2024-07-15', estimatedValue: 1200 },
        { id: '4', name: 'Diego Torres', phone: '+593987654324', email: 'diego@email.com', source: 'Google', status: 'Contacted', createdAt: '2024-07-12', estimatedValue: 600 },
        { id: '5', name: 'Lucía Morales', phone: '+593987654325', email: 'lucia@email.com', source: 'Facebook', status: 'Lost', createdAt: '2024-07-10', estimatedValue: 400 }
    ]);

    const [campaigns, setCampaigns] = useState([
        { id: '1', name: 'Campaña Verano 2024', platform: 'Facebook', budget: 5000, spent: 3200, revenue: 8500, leads: 125, clients: 28, started: '2024-06-01', status: 'Active' },
        { id: '2', name: 'Instagram Stories', platform: 'Instagram', budget: 3000, spent: 2800, revenue: 7200, leads: 98, clients: 22, started: '2024-06-15', status: 'Active' },
        { id: '3', name: 'Google Ads Q2', platform: 'Google', budget: 8000, spent: 7500, revenue: 15800, leads: 186, clients: 41, started: '2024-04-01', status: 'Completed' }
    ]);

    const [filters, setFilters] = useState({
        leadSource: '',
        leadStatus: '',
        campaignPlatform: ''
    });

    const [showCreateLead, setShowCreateLead] = useState(false);
    const [showCreateCampaign, setShowCreateCampaign] = useState(false);

    // Datos para gráficos
    const revenueData = [
        { month: 'Ene', revenue: 8500, spent: 3200 },
        { month: 'Feb', revenue: 12300, spent: 4100 },
        { month: 'Mar', revenue: 15800, spent: 5200 },
        { month: 'Apr', revenue: 18200, spent: 6100 },
        { month: 'May', revenue: 22100, spent: 7300 },
        { month: 'Jun', revenue: 25600, spent: 8400 },
        { month: 'Jul', revenue: 22400, spent: 6800 }
    ];

    const leadSourceData = [
        { name: 'Facebook', value: 320, color: '#1877F2' },
        { name: 'Instagram', value: 280, color: '#E4405F' },
        { name: 'WhatsApp', value: 150, color: '#25D366' },
        { name: 'Google', value: 100, color: '#4285F4' }
    ];

    const conversionFunnelData = [
        { stage: 'Leads', count: 850, color: '#3B82F6' },
        { stage: 'Contactados', count: 650, color: '#10B981' },
        { stage: 'Calificados', count: 320, color: '#F59E0B' },
        { stage: 'Convertidos', count: 187, color: '#EF4444' }
    ];

    const getStatusColor = (status) => {
        const colors = {
            'New': 'bg-blue-100 text-blue-800',
            'Contacted': 'bg-yellow-100 text-yellow-800',
            'Qualified': 'bg-purple-100 text-purple-800',
            'Converted': 'bg-green-100 text-green-800',
            'Lost': 'bg-red-100 text-red-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD' }).format(amount);
    };

    const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
        <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className={`text-2xl font-bold ${color}`}>{value}</p>
                    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
                </div>
                <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
                    <Icon className={`h-6 w-6 ${color}`} />
                </div>
            </div>
        </div>
    );

    const CreateLeadModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">Crear Nuevo Lead</h3>
                <div className="space-y-4">
                    <input className="w-full p-2 border rounded-lg" placeholder="Nombre completo" />
                    <input className="w-full p-2 border rounded-lg" placeholder="Teléfono" />
                    <input className="w-full p-2 border rounded-lg" placeholder="Email" />
                    <select className="w-full p-2 border rounded-lg">
                        <option value="">Seleccionar fuente</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="WhatsApp">WhatsApp</option>
                        <option value="Google">Google</option>
                    </select>
                    <input className="w-full p-2 border rounded-lg" placeholder="Valor estimado" type="number" />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                    <button onClick={() => setShowCreateLead(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        Cancelar
                    </button>
                    <button onClick={() => setShowCreateLead(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        Crear Lead
                    </button>
                </div>
            </div>
        </div>
    );

    const CreateCampaignModal = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h3 className="text-lg font-semibold mb-4">Crear Nueva Campaña</h3>
                <div className="space-y-4">
                    <input className="w-full p-2 border rounded-lg" placeholder="Nombre de campaña" />
                    <select className="w-full p-2 border rounded-lg">
                        <option value="">Seleccionar plataforma</option>
                        <option value="Facebook">Facebook</option>
                        <option value="Instagram">Instagram</option>
                        <option value="Google">Google</option>
                    </select>
                    <input className="w-full p-2 border rounded-lg" placeholder="Presupuesto" type="number" />
                    <input className="w-full p-2 border rounded-lg" type="date" />
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                    <button onClick={() => setShowCreateCampaign(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
                        Cancelar
                    </button>
                    <button onClick={() => setShowCreateCampaign(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        Crear Campaña
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4">
                        <h1 className="text-2xl font-bold text-gray-900">Panel de Marketing</h1>
                        <div className="flex items-center space-x-4">
                            <select
                                value={selectedBusiness}
                                onChange={(e) => setSelectedBusiness(e.target.value)}
                                className="border rounded-lg px-3 py-2"
                            >
                                <option value="business-1">Mi Negocio</option>
                                <option value="business-2">Negocio 2</option>
                            </select>
                            <div className="flex items-center space-x-2">
                                <Calendar className="h-4 w-4 text-gray-500" />
                                <input
                                    type="date"
                                    value={dateRange.from}
                                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                                    className="border rounded px-2 py-1 text-sm"
                                />
                                <span className="text-gray-500">-</span>
                                <input
                                    type="date"
                                    value={dateRange.to}
                                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                                    className="border rounded px-2 py-1 text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'overview', name: 'Resumen', icon: TrendingUp },
                            { id: 'leads', name: 'Leads', icon: Users },
                            { id: 'campaigns', name: 'Campañas', icon: Target },
                            { id: 'analytics', name: 'Analíticas', icon: DollarSign }
                        ].map(({ id, name, icon: Icon }) => (
                            <button
                                key={id}
                                onClick={() => setActiveTab(id)}
                                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${activeTab === id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span>{name}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {activeTab === 'overview' && (
                    <div className="space-y-8">
                        {/* Métricas principales */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <MetricCard
                                title="Ingresos Totales"
                                value={formatCurrency(metrics.revenue)}
                                icon={DollarSign}
                                color="text-green-600"
                                subtitle={`ROAS: ${metrics.roas}x`}
                            />
                            <MetricCard
                                title="Gastado en Ads"
                                value={formatCurrency(metrics.spent)}
                                icon={Target}
                                color="text-red-600"
                                subtitle="Este período"
                            />
                            <MetricCard
                                title="Total Leads"
                                value={metrics.leads.toLocaleString()}
                                icon={Users}
                                color="text-blue-600"
                                subtitle={`${metrics.conversionRate}% conversión`}
                            />
                            <MetricCard
                                title="Clientes"
                                value={metrics.clients}
                                icon={TrendingUp}
                                color="text-purple-600"
                                subtitle={`CAC: ${formatCurrency(metrics.cac)}`}
                            />
                        </div>

                        {/* Gráfico de ingresos */}
                        <div className="bg-white rounded-lg shadow-sm border p-6">
                            <h3 className="text-lg font-semibold mb-4">Ingresos vs Gastos</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={revenueData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip formatter={(value) => formatCurrency(value)} />
                                    <Legend />
                                    <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={3} name="Ingresos" />
                                    <Line type="monotone" dataKey="spent" stroke="#EF4444" strokeWidth={3} name="Gastos" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Fuentes de leads */}
                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <h3 className="text-lg font-semibold mb-4">Fuentes de Leads</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={leadSourceData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {leadSourceData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Embudo de conversión */}
                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <h3 className="text-lg font-semibold mb-4">Embudo de Conversión</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={conversionFunnelData}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="stage" />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="count" fill="#3B82F6" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'leads' && (
                    <div className="space-y-6">
                        {/* Header con filtros */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                            <h2 className="text-xl font-semibold">Gestión de Leads</h2>
                            <button
                                onClick={() => setShowCreateLead(true)}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Nuevo Lead</span>
                            </button>
                        </div>

                        {/* Filtros */}
                        <div className="bg-white rounded-lg shadow-sm border p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar leads..."
                                        className="pl-10 w-full p-2 border rounded-lg"
                                    />
                                </div>
                                <select
                                    value={filters.leadSource}
                                    onChange={(e) => setFilters({ ...filters, leadSource: e.target.value })}
                                    className="p-2 border rounded-lg"
                                >
                                    <option value="">Todas las fuentes</option>
                                    <option value="Facebook">Facebook</option>
                                    <option value="Instagram">Instagram</option>
                                    <option value="WhatsApp">WhatsApp</option>
                                    <option value="Google">Google</option>
                                </select>
                                <select
                                    value={filters.leadStatus}
                                    onChange={(e) => setFilters({ ...filters, leadStatus: e.target.value })}
                                    className="p-2 border rounded-lg"
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="New">Nuevos</option>
                                    <option value="Contacted">Contactados</option>
                                    <option value="Qualified">Calificados</option>
                                    <option value="Converted">Convertidos</option>
                                    <option value="Lost">Perdidos</option>
                                </select>
                            </div>
                        </div>

                        {/* Tabla de leads */}
                        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Lead
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Contacto
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fuente
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Estado
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Valor Est.
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Fecha
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Acciones
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {leads.map((lead) => (
                                            <tr key={lead.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm font-medium text-gray-900">{lead.name}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <div className="text-sm text-gray-900">{lead.phone}</div>
                                                    <div className="text-sm text-gray-500">{lead.email}</div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {lead.source}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead.status)}`}>
                                                        {lead.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {formatCurrency(lead.estimatedValue)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {new Date(lead.createdAt).toLocaleDateString('es-EC')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex space-x-2">
                                                        <button className="text-blue-600 hover:text-blue-900">
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                        <button className="text-gray-600 hover:text-gray-900">
                                                            <Edit className="h-4 w-4" />
                                                        </button>
                                                        {lead.status === 'New' || lead.status === 'Qualified' ? (
                                                            <button
                                                                onClick={() => {
                                                                    const updatedLeads = leads.map(l =>
                                                                        l.id === lead.id ? { ...l, status: 'Converted', convertedAt: new Date().toISOString().split('T')[0] } : l
                                                                    );
                                                                    setLeads(updatedLeads);
                                                                }}
                                                                className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-2 py-1 rounded"
                                                            >
                                                                Convertir
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'campaigns' && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold">Campañas Publicitarias</h2>
                            <button
                                onClick={() => setShowCreateCampaign(true)}
                                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                            >
                                <Plus className="h-4 w-4" />
                                <span>Nueva Campaña</span>
                            </button>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                            {campaigns.map((campaign) => (
                                <div key={campaign.id} className="bg-white rounded-lg shadow-sm border p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-lg font-semibold">{campaign.name}</h3>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${campaign.status === 'Active' ? 'bg-green-100 text-green-800' :
                                                campaign.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
                                                    'bg-gray-100 text-gray-800'
                                            }`}>
                                            {campaign.status}
                                        </span>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Plataforma:</span>
                                            <span className="font-medium">{campaign.platform}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Presupuesto:</span>
                                            <span className="font-medium">{formatCurrency(campaign.budget)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Gastado:</span>
                                            <span className="font-medium">{formatCurrency(campaign.spent)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Ingresos:</span>
                                            <span className="font-medium text-green-600">{formatCurrency(campaign.revenue)}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">ROAS:</span>
                                            <span className="font-medium">{(campaign.revenue / campaign.spent).toFixed(2)}x</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Leads:</span>
                                            <span className="font-medium">{campaign.leads}</span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-gray-600">Conversiones:</span>
                                            <span className="font-medium">{campaign.clients}</span>
                                        </div>
                                    </div>

                                    <div className="mt-4 pt-4 border-t">
                                        <div className="flex justify-between items-center text-xs text-gray-500">
                                            <span>Iniciada: {new Date(campaign.started).toLocaleDateString('es-EC')}</span>
                                            <span>{((campaign.clients / campaign.leads) * 100).toFixed(1)}% conversión</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'analytics' && (
                    <div className="space-y-8">
                        <h2 className="text-xl font-semibold">Analíticas Avanzadas</h2>

                        {/* ROI por campaña */}
                        <div className="bg-white rounded-lg shadow-sm border p-6">
                            <h3 className="text-lg font-semibold mb-4">ROI por Campaña</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={campaigns.map(c => ({
                                    name: c.name,
                                    roi: ((c.revenue - c.spent) / c.spent * 100),
                                    roas: c.revenue / c.spent
                                }))}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip formatter={(value, name) => [
                                        name === 'roi' ? `${value.toFixed(1)}%` : `${value.toFixed(2)}x`,
                                        name === 'roi' ? 'ROI' : 'ROAS'
                                    ]} />
                                    <Legend />
                                    <Bar dataKey="roi" fill="#10B981" name="ROI %" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Métricas de performance */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <h4 className="text-md font-semibold mb-3">Costo por Lead (CPL)</h4>
                                <div className="space-y-2">
                                    {campaigns.map(campaign => (
                                        <div key={campaign.id} className="flex justify-between">
                                            <span className="text-sm text-gray-600">{campaign.platform}</span>
                                            <span className="text-sm font-medium">{formatCurrency(campaign.spent / campaign.leads)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <h4 className="text-md font-semibold mb-3">Costo de Adquisición (CAC)</h4>
                                <div className="space-y-2">
                                    {campaigns.map(campaign => (
                                        <div key={campaign.id} className="flex justify-between">
                                            <span className="text-sm text-gray-600">{campaign.platform}</span>
                                            <span className="text-sm font-medium">{formatCurrency(campaign.spent / campaign.clients)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-white rounded-lg shadow-sm border p-6">
                                <h4 className="text-md font-semibold mb-3">Tasa de Conversión</h4>
                                <div className="space-y-2">
                                    {campaigns.map(campaign => (
                                        <div key={campaign.id} className="flex justify-between">
                                            <span className="text-sm text-gray-600">{campaign.platform}</span>
                                            <span className="text-sm font-medium">{((campaign.clients / campaign.leads) * 100).toFixed(1)}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Tendencias temporales */}
                        <div className="bg-white rounded-lg shadow-sm border p-6">
                            <h3 className="text-lg font-semibold mb-4">Tendencia de Leads por Mes</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={[
                                    { month: 'Ene', leads: 45, conversions: 12 },
                                    { month: 'Feb', leads: 67, conversions: 18 },
                                    { month: 'Mar', leads: 89, conversions: 24 },
                                    { month: 'Apr', leads: 112, conversions: 31 },
                                    { month: 'May', leads: 134, conversions: 38 },
                                    { month: 'Jun', leads: 156, conversions: 42 },
                                    { month: 'Jul', leads: 147, conversions: 37 }
                                ]}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={3} name="Leads" />
                                    <Line type="monotone" dataKey="conversions" stroke="#10B981" strokeWidth={3} name="Conversiones" />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Performance por fuente */}
                        <div className="bg-white rounded-lg shadow-sm border p-6">
                            <h3 className="text-lg font-semibold mb-4">Performance por Fuente de Lead</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-3 px-4">Fuente</th>
                                            <th className="text-left py-3 px-4">Leads</th>
                                            <th className="text-left py-3 px-4">Conversiones</th>
                                            <th className="text-left py-3 px-4">Tasa Conv.</th>
                                            <th className="text-left py-3 px-4">Valor Promedio</th>
                                            <th className="text-left py-3 px-4">Revenue Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { source: 'Facebook', leads: 320, conversions: 78, avgValue: 650, total: 50700 },
                                            { source: 'Instagram', leads: 280, conversions: 65, avgValue: 580, total: 37700 },
                                            { source: 'WhatsApp', leads: 150, conversions: 28, avgValue: 720, total: 20160 },
                                            { source: 'Google', leads: 100, conversions: 16, avgValue: 820, total: 13120 }
                                        ].map((row) => (
                                            <tr key={row.source} className="border-b hover:bg-gray-50">
                                                <td className="py-3 px-4 font-medium">{row.source}</td>
                                                <td className="py-3 px-4">{row.leads}</td>
                                                <td className="py-3 px-4">{row.conversions}</td>
                                                <td className="py-3 px-4">{((row.conversions / row.leads) * 100).toFixed(1)}%</td>
                                                <td className="py-3 px-4">{formatCurrency(row.avgValue)}</td>
                                                <td className="py-3 px-4 font-semibold text-green-600">{formatCurrency(row.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modales */}
            {showCreateLead && <CreateLeadModal />}
            {showCreateCampaign && <CreateCampaignModal />}
        </div>
    );
};

export default Panel;